/**
 * Quote Update Request Panel
 * Displays pending update request with Ops-readable diffs and accept/reject flows.
 */

import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Alert,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Divider,
} from "@mui/material";
import { message } from "antd";
import {
  acceptQuoteUpdateRequest,
  rejectQuoteUpdateRequest,
} from "../services/quoteUpdateRequestService.js";
import {
  buildChangeDisplayRows,
  formatRelativeSubmittedAt,
} from "../utils/formatQuoteUpdateChanges.js";
import { logError } from "@/shared/utils/logger.js";

/**
 * @param {{ type: string, label: string, fromQty?: number, toQty?: number }} row
 */
const ItemDiffRow = ({ row }) => {
  if (row.type === "added") {
    return (
      <Typography variant="body2" sx={{ color: "success.dark", fontWeight: 500 }}>
        + {row.toQty > 1 ? `${row.toQty} × ` : ""}
        {row.label}
      </Typography>
    );
  }
  if (row.type === "removed") {
    return (
      <Typography
        variant="body2"
        sx={{ color: "error.main", textDecoration: "line-through", opacity: 0.85 }}
      >
        − {row.fromQty > 1 ? `${row.fromQty} × ` : ""}
        {row.label}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" sx={{ color: "text.primary" }}>
      Quantity changed: {row.label} {row.fromQty} → {row.toQty}
    </Typography>
  );
};

/**
 * @param {{ label: string, fromDisplay: string, toDisplay: string, itemDiff: Array|null }} change
 */
const ChangeRow = ({ change }) => (
  <Box
    sx={{
      py: 1.5,
      borderBottom: "1px solid",
      borderColor: "divider",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
      {change.label}
    </Typography>

    {change.itemDiff ? (
      <Stack spacing={0.5} sx={{ pl: 0.5 }}>
        {change.itemDiff.map((row, idx) => (
          <ItemDiffRow key={`${row.type}-${row.label}-${idx}`} row={row} />
        ))}
      </Stack>
    ) : (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 0.5, sm: 1.5 }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        flexWrap="wrap"
      >
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            textDecoration: "line-through",
            opacity: 0.8,
            maxWidth: "100%",
            wordBreak: "break-word",
          }}
        >
          {change.fromDisplay}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.disabled", display: { xs: "none", sm: "inline" } }}
        >
          →
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "success.dark",
            fontWeight: 600,
            maxWidth: "100%",
            wordBreak: "break-word",
          }}
        >
          {change.toDisplay}
        </Typography>
      </Stack>
    )}
  </Box>
);

/**
 * Quote Update Request Panel Component
 */
const QuoteUpdateRequestPanel = ({ quoteId, request, onResolved }) => {
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [priceAction, setPriceAction] = useState("keep");
  const [adjustedQuoteValue, setAdjustedQuoteValue] = useState("");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const changeRows = useMemo(
    () => buildChangeDisplayRows(request?.changes),
    [request]
  );

  const submittedRelative = useMemo(
    () => formatRelativeSubmittedAt(request?.submittedAt),
    [request]
  );

  const summaryText = useMemo(() => {
    const count = changeRows.length;
    const countPart =
      count === 0
        ? "Customer submitted updated information"
        : `Customer requested ${count} change${count === 1 ? "" : "s"}`;
    if (submittedRelative) {
      return `${countPart} · received ${submittedRelative}`;
    }
    return `${countPart}. Please review and accept or reject.`;
  }, [changeRows.length, submittedRelative]);

  const handleAccept = async () => {
    if (priceAction === "update" && adjustedQuoteValue === "") {
      message.error("Please enter a new total price.");
      return;
    }

    if (priceAction === "update") {
      const priceNum = Number(adjustedQuoteValue);
      if (isNaN(priceNum) || priceNum <= 0) {
        message.error("Please enter a valid price greater than 0.");
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        priceAction,
        adjustedQuoteValue:
          priceAction === "update" ? Number(adjustedQuoteValue) : undefined,
        resolutionNotes,
      };

      const result = await acceptQuoteUpdateRequest(
        quoteId,
        request.id,
        payload
      );

      if (result.success) {
        message.success("Update accepted and applied.");
        onResolved?.();
        setAcceptOpen(false);
        setPriceAction("keep");
        setAdjustedQuoteValue("");
        setResolutionNotes("");
      } else {
        message.error(result.error || "Failed to accept update.");
      }
    } catch (error) {
      logError("Error accepting quote update request", {
        quoteId,
        requestId: request.id,
        error: error.message,
      });
      message.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    setLoading(true);
    try {
      const payload = { resolutionNotes };
      const result = await rejectQuoteUpdateRequest(
        quoteId,
        request.id,
        payload
      );

      if (result.success) {
        message.success("Update rejected.");
        onResolved?.();
        setRejectOpen(false);
        setResolutionNotes("");
      } else {
        message.error(result.error || "Failed to reject update.");
      }
    } catch (error) {
      logError("Error rejecting quote update request", {
        quoteId,
        requestId: request.id,
        error: error.message,
      });
      message.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card elevation={1} sx={{ mb: 3, borderLeft: "4px solid #fa8c16" }}>
        <CardContent sx={{ p: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {summaryText}
          </Alert>

          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Requested Changes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Compare current quote details with what the customer submitted.
          </Typography>

          <Box data-testid="list">
            {changeRows.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No detailed change data available.
              </Typography>
            )}
            {changeRows.map((change) => (
              <Box key={change.key} data-testid="list-item">
                <ChangeRow change={change} />
              </Box>
            ))}
          </Box>

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="success"
              data-testid="button-accept"
              onClick={() => setAcceptOpen(true)}
              disabled={loading}
            >
              Approve and apply changes
            </Button>
            <Button
              variant="outlined"
              color="error"
              data-testid="button-reject"
              onClick={() => setRejectOpen(true)}
              disabled={loading}
            >
              Decline request
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Accept Dialog */}
      <Dialog open={acceptOpen} onClose={() => !loading && setAcceptOpen(false)}>
        <DialogTitle>Confirm Acceptance</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose whether to keep the same price or apply a new price before
            accepting.
          </Typography>

          {changeRows.length > 0 && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                bgcolor: "grey.50",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                Changes that will be applied
              </Typography>
              {changeRows.map((change, idx) => (
                <React.Fragment key={change.key}>
                  {idx > 0 && <Divider sx={{ my: 1 }} />}
                  <Typography variant="body2" fontWeight={600}>
                    {change.label}
                  </Typography>
                  {change.itemDiff ? (
                    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                      {change.itemDiff.map((row, i) => (
                        <ItemDiffRow key={`${row.type}-${i}`} row={row} />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {change.fromDisplay} → {change.toDisplay}
                    </Typography>
                  )}
                </React.Fragment>
              ))}
            </Box>
          )}

          <RadioGroup
            value={priceAction}
            onChange={(e) => setPriceAction(e.target.value)}
          >
            <FormControlLabel
              value="keep"
              control={<Radio />}
              label="Keep current price"
            />
            <FormControlLabel
              value="update"
              control={<Radio />}
              label="Update price"
            />
          </RadioGroup>

          {priceAction === "update" && (
            <TextField
              fullWidth
              type="number"
              label="New Total Price"
              value={adjustedQuoteValue}
              onChange={(e) => setAdjustedQuoteValue(e.target.value)}
              sx={{ mt: 2 }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          )}

          <TextField
            fullWidth
            label="Confirmation notes (optional)"
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.target.value)}
            sx={{ mt: 2 }}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAccept}
            disabled={loading}
            color="success"
          >
            Confirm Accept
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onClose={() => !loading && setRejectOpen(false)}>
        <DialogTitle>Confirm Rejection</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to reject this update request?
          </Typography>
          <TextField
            fullWidth
            label="Rejection notes (optional)"
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={loading}
          >
            Confirm Reject
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuoteUpdateRequestPanel;
